import { useState, useMemo } from 'react';

import {
  Employer,
  ContentNugget,
  mapCareerPathResponseToCareerPath,
  CareerPathResponse,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  FilterButton,
  FilterOption,
} from '@backrs_ui/grow-ui/src/components/ResourceWrapper/FilterButton';
import { setCurrentDialogNugget } from '@backrs_ui/grow-ui/src/redux/contentNugget/contentNugget';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';

import { Box, Typography, Chip, SxProps, Theme } from '@mui/material';

type CareerPathSectionProps = Readonly<{
  employer: Employer;
}>;

const SECTION_STRINGS = appStrings.modules.employer.careerPath;

const groupAndSortCareerPaths = (careerPaths: CareerPathResponse[]) => {
  const grouped = careerPaths.reduce(
    (acc, careerPath) => {
      const categoryName =
        careerPath.career_category?.name || `Category ${careerPath.career_category_id}`;

      if (!acc[categoryName]) {
        acc[categoryName] = [];
      }

      acc[categoryName].push(careerPath);

      return acc;
    },
    {} as Record<string, typeof careerPaths>,
  );

  // sort categories by descending number of career paths
  return Object.entries(grouped).sort(
    ([, careerPathsA], [, careerPathsB]) => careerPathsB.length - careerPathsA.length,
  );
};

const createMinimumTrackFilterOptions = (careerPaths: CareerPathResponse[]): FilterOption[] => {
  const allTracks = careerPaths.map((careerPath) => careerPath.minimum_track_name);

  const uniqueTracks = allTracks.filter((track, index, array) => array.indexOf(track) === index);

  return uniqueTracks
    .filter((track) => track !== undefined)
    .map((track) => ({
      key: track,
      label: track,
    }));
};

export const CareerPathSection = ({ employer }: CareerPathSectionProps) => {
  const dispatch = useAppDispatch();
  const [selectedMinimumTracks, setSelectedMinimumTracks] = useState<FilterOption[]>([]);

  const handleCareerPathClick = (careerPath: CareerPathResponse) => {
    const careerPathNugget: ContentNugget = {
      id: careerPath.id,
      slug: careerPath.name.toLowerCase().replace(/\s+/g, '-'),
      title: careerPath.name,
      description: careerPath.description || '',
      variant: 'career-path',
      is_browsable: true,
      star_rating: 0,
      value_rating: 0,
      career_path_info: mapCareerPathResponseToCareerPath(careerPath),
    };

    dispatch(setCurrentDialogNugget(careerPathNugget));
  };

  const minimumTrackOptions = createMinimumTrackFilterOptions(employer.career_paths);

  const headerButton =
    minimumTrackOptions.length > 1 ? (
      <FilterButton
        name={SECTION_STRINGS.filterByTrack}
        iconName="FilterLines"
        options={minimumTrackOptions}
        selectedValues={selectedMinimumTracks}
        onSelectionChange={setSelectedMinimumTracks}
      />
    ) : null;

  const filteredCareerPaths = useMemo(() => {
    if (selectedMinimumTracks.length === 0) {
      return employer.career_paths;
    }

    return employer.career_paths.filter(
      (careerPath) =>
        careerPath.minimum_track_name &&
        selectedMinimumTracks.some(
          (selectedTrack) => selectedTrack.key === careerPath.minimum_track_name,
        ),
    );
  }, [employer.career_paths, selectedMinimumTracks]);

  const sortedGroupedCareerPaths = groupAndSortCareerPaths(filteredCareerPaths);

  return (
    <Box sx={containerSx}>
      <Box sx={titleContainerSx}>
        <Box sx={titleSx}>
          <Typography variant="display-sm-medium">{SECTION_STRINGS.title}</Typography>
          <Box sx={largeScreenButtonSx}>{headerButton}</Box>
        </Box>
        <Box sx={summaryContainerSx}>
          <Typography sx={summarySx} variant="text-sm-regular" component="div">
            {employer.the_scoop_roles}
          </Typography>
          <Box sx={smallScreenButtonSx}>{headerButton}</Box>
        </Box>
      </Box>

      <Box sx={categoriesGridSx}>
        {sortedGroupedCareerPaths.map(([categoryName, careerPaths]) => (
          <Box key={categoryName}>
            <Typography variant="text-md-semibold" sx={categoryTitleSx}>
              {categoryName}
            </Typography>
            <Box sx={chipsContainerSx}>
              {careerPaths.map((careerPath) => {
                return (
                  <Chip
                    key={careerPath.id}
                    size="sm"
                    label={careerPath.name}
                    variant="outlined"
                    clickable
                    sx={chipSx}
                    onClick={() => handleCareerPathClick(careerPath)}
                  />
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  mb: backrs.spacing['spacing-6xl'],
  borderTop: {
    xs: `1px solid ${backrs.colors['Colors/Foreground/fg-quaternary']}`,
    sm: 'none',
  },
  marginX: backrs.spacing['spacing-4xl'],
  '@media (max-height: 768px)': {
    mx: backrs.spacing['spacing-4xl'],
  },
  '@media (max-width: 414px)': {
    mx: backrs.spacing['spacing-2xl'],
  },
});

const titleContainerSx: SxProps<Theme> = ({ backrs }) => ({
  marginY: backrs.spacing['spacing-2xl'],
  display: 'flex',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  flexDirection: 'column',
});

const titleSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  flexWrap: 'wrap',
};

const summarySx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  padding: 0,
  margin: 0,
});

const summaryContainerSx: SxProps<Theme> = {
  width: { xs: '100%', sm: '66%', lg: '66%' },
  padding: 0,
  margin: 0,
};

const categoriesGridSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    sm: 'repeat(2, 1fr)',
    md: 'repeat(3, 1fr)',
  },
  gap: backrs.spacing['spacing-2xl'],
  alignItems: 'flex-start',
});

const categoryTitleSx: SxProps<Theme> = ({ backrs }) => ({
  mb: backrs.spacing['spacing-2xl'],
  color: backrs.colors['Colors/Foreground/fg-brand-secondary'],
});

const chipsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
  alignItems: 'flex-start',
});

const chipSx: SxProps<Theme> = ({ backrs, typography }) => ({
  height: 'auto',
  '& .MuiChip-label': {
    ...typography['text-sm-medium'],
    color: backrs.colors['Gray/700'],
    paddingTop: backrs.spacing['spacing-xxs'],
    paddingBottom: backrs.spacing['spacing-xxs'],
    paddingLeft: backrs.spacing['spacing-md'],
    paddingRight: backrs.spacing['spacing-md'],
  },
});

const largeScreenButtonSx: SxProps<Theme> = ({ backrs }) => ({
  display: { xs: 'none', sm: 'block' },
  alignSelf: 'flex-end',
  marginBottom: backrs.spacing['spacing-xs'],
});

const smallScreenButtonSx: SxProps<Theme> = ({ backrs }) => ({
  display: { xs: 'block', sm: 'none' },
  marginTop: backrs.spacing['spacing-xl'],
  width: 'fit-content',
});
